import { useMemo } from "react";

import { addDays, isFuture } from "date-fns";
import { useSelector } from "react-redux";

import { useObjectMemo } from "../../../hooks";
import { useFeature } from "../features";
import filters from "../../../../filters";
import {
  selectBAccount,
  selectMinPaymentDueDate,
  selectMinimumPayment,
} from "../../entities/accounts/selectors";
import { isLoan } from "../../entities/accounts";
import { selectInstitution } from "../../entities/selectors/institution.selectors";

import { loanPaymentForm } from "./LoanPayment.form";

import type { RootState } from "../../entities/store";

export const useLoanPaymentEnabled = () => {
  const { featureEnabled } = useFeature();
  const hasTransferAccess = useMemo(
    () =>
      featureEnabled({
        or: ["ach", "internal_transfers"],
      }),
    [featureEnabled],
  );

  return useObjectMemo({
    isMobileLoanPaymentsEnabled:
      hasTransferAccess && featureEnabled("enable_loan_payments_mobile"),
    isWebLoanPaymentsEnabled:
      hasTransferAccess && featureEnabled("enable_loan_payments_web"),
    isPrincipalPaymentEnabled:
      hasTransferAccess && featureEnabled("loan_principal_payment"),
    isCustomPaymentEnabled:
      hasTransferAccess && featureEnabled("loan_custom_payment"),
    isPaymentByCardEnabled:
      hasTransferAccess && featureEnabled("loan_payment_by_card"),
  });
};

export const useLoanPaymentForm = () => {
  const form = loanPaymentForm.useForm();
  const { values } = form;
  const {
    toAccountId: selectedToAccountId,
    paymentType: selectedPaymentType,
    fundingMethod: selectedFundingMethod,
  } = values;
  const isFixedAmount = selectedPaymentType === "amountDue";

  return useObjectMemo({
    form,
    /** Flags for determining which elements of the form should appear */
    formMetadata: {
      isFixedAmount,
      isSelectPaymentTypeVisible: !!selectedToAccountId,
      isPaymentDetailsVisible: !!selectedPaymentType,
      isPaymentDetailsPrincipalVisible: selectedPaymentType === "principal",
      isPaymentDetailsCardVisible:
        selectedFundingMethod === "card" && selectedPaymentType !== "principal",
      isPaymentDetailsBankVisible:
        selectedFundingMethod === "bank" && selectedPaymentType !== "principal",
    },
  });
};

/**
 * @deprecated Refer to `useLoanPaymentEnabled` for loan related permissions
 */
export const useLoanCanPayPrincipalOnly = (accountId: API.AccountId) => {
  const { featureEnabled } = useFeature();

  const isValidLoan = useSelector((state: RootState) => {
    const account = selectBAccount(state, accountId);
    return isLoan(account);
  });

  return (
    featureEnabled({
      and: ["internal_transfers", "loan_principal_payment"],
    }) && isValidLoan
  );
};

export const useLoanMinimumPayment = (accountId: API.AccountId) => {
  const minimumPayment = useSelector((state: RootState) => {
    if (accountId) {
      return selectMinimumPayment(state, accountId) || (0 as Cents);
    }
    return 0 as Cents;
  });

  const dueDate = useSelector((state: RootState) => {
    return selectMinPaymentDueDate(state, accountId) || "";
  });

  const institution = useSelector(selectInstitution);

  const formattedDueDate = useMemo(
    () => (dueDate ? filters.shortDate(dueDate) : ""),
    [dueDate],
  );

  const isPaymentPastDue = useMemo(() => {
    if (!dueDate) return false;
    if (minimumPayment <= 0) return false;

    const overdueThreshold = addDays(
      new Date(dueDate),
      institution?.loan_payment_overdue_days || 1,
    );
    return !isFuture(overdueThreshold);
  }, [dueDate, minimumPayment, institution?.loan_payment_overdue_days]);

  return useObjectMemo({
    minimumPayment,
    formattedDueDate,
    isPaymentPastDue,
    formattedMinimumPayment: filters.currency(minimumPayment),
  });
};
